/**
 * DataSourceAssistantPane — ask this data source a question, beside the files.
 *
 * This was the "Test" TAB: a full-width transcript that replaced the file
 * list you were asking about, and that was thrown away every time you looked
 * at something else. It is the matter Assistant's shape — a conversation read
 * against the work rather than instead of it — so it is a pane.
 *
 * IT IS A COMPACT CHAT, NOT A RESEARCH SESSION. `MatterAssistantPane` composes
 * `useResearchSession` + `ResearchTranscript` + `ResearchComposer`, because a
 * matter's assistant IS a research session: it has turns, versions, ratings,
 * citations, attachments and a chat group on the server. The data-source Test
 * chat is one POST to `/datasources/:uuid/chat` returning a string, with the
 * history sent back each time and nothing persisted. Reusing that stack would
 * mean creating research chat groups this endpoint knows nothing about. So
 * the pane keeps the existing send/endpoint logic exactly as it was and
 * borrows the LAYOUT — a header line, a scrolling transcript, a compact
 * composer pinned to the bottom.
 *
 * IT OWNS NO STATE. The transcript, the draft and the in-flight flag all live
 * on the page and arrive as props. That is deliberate and load-bearing: a
 * pane body is remounted when its pane is dragged from the right dock to the
 * left, and a conversation that disappeared on a dock move would be worse
 * than the tab this replaces. See the header of `AIStudioIndexDetailPage`.
 */

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PiChatsCircleLight, PiPaperPlaneLight, PiSparkleLight } from 'react-icons/pi';
import BrandLoader from '../../../shared/brand/BrandLoader';
import Badge from '../../../shared/TailwindComponents/Badge';
import Button from '../../../shared/TailwindComponents/Button';
import Input from '../../../shared/TailwindComponents/Input';

export interface DataSourceChatMessage {
  role: 'user' | 'dataSource';
  content: string;
}

interface Props {
  messages: DataSourceChatMessage[];
  input: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  isSending: boolean;
  /**
   * This data source cannot answer yet — it is still processing, or has no
   * files. The pane says so instead of sending a question the index has
   * nothing to match. (It used to mean "not live in this environment"; the
   * chat endpoint is live everywhere now.)
   */
  disabled?: boolean;
  disabledLabel?: string;
}

const DataSourceAssistantPane: React.FC<Props> = ({
  messages,
  input,
  onInputChange,
  onSend,
  isSending,
  disabled = false,
  disabledLabel,
}) => {
  const { t } = useTranslation();
  const transcriptRef = useRef<HTMLDivElement>(null);

  /*
    THE TRANSCRIPT SCROLLS ITSELF. Nothing else moves.

    This was `endRef.current.scrollIntoView()`, and that is the bug behind
    "clicking Test scrolls the whole section away and I can't get it back".
    `scrollIntoView` does not scroll one box — it walks EVERY scrollable
    ancestor and moves each one until the node is in view. The transcript sits
    inside a pane, inside the detail surface, inside the page, so the browser
    obliged by scrolling all of them. The last turn came into view and the page
    went with it.

    It also fired on mount, before there was a single message: opening the pane
    was enough to throw the page, which is what made it look intermittent — it
    only shows when the page has somewhere to scroll TO.

    Setting `scrollTop` moves exactly one box and cannot touch an ancestor. And
    only when the transcript has something to scroll, so an empty pane leaves
    the page where the reader left it.
  */
  useEffect(() => {
    const box = transcriptRef.current;
    if (!box) return;
    if (box.scrollHeight <= box.clientHeight) return;
    box.scrollTo({ top: box.scrollHeight, behavior: 'smooth' });
  }, [messages, isSending]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!disabled) onSend();
    }
  };

  return (
    <div
      data-testid="datasource-assistant-pane"
      data-tour-id="ds-test-chat"
      className="relative flex flex-col h-full min-h-0"
    >
      {disabled && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/70 backdrop-blur-[1px]">
          <Badge color="gray">{disabledLabel || t('Available once processing completes')}</Badge>
        </div>
      )}

      <div
        ref={transcriptRef}
        className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main px-3 py-3 space-y-3"
      >
        {messages.length === 0 && !isSending ? (
          <div
            data-testid="datasource-assistant-empty"
            className="h-full flex flex-col items-center justify-center gap-3 px-4 py-8 text-center"
          >
            <span className="h-12 w-12 rounded-full bg-surface-sunken border border-hairline-soft
                             flex items-center justify-center">
              <PiChatsCircleLight className="h-6 w-6 text-champagne" aria-hidden />
            </span>
            <div className="space-y-1">
              <p className="text-sm font-medium text-fg-body">{t('Test this data source')}</p>
              <p className="text-xs text-fg-muted leading-relaxed max-w-[17rem]">
                {t('Start the conversation by entering a prompt below.')}
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[10px] text-fg-muted">
              <PiSparkleLight className="h-3 w-3" aria-hidden />
              {t('Answers come only from the files in this data source')}
            </span>
          </div>
        ) : (
          messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={
                  msg.role === 'user'
                    ? 'p-2.5 rounded-lg bg-surface-sunken text-fg-body w-fit max-w-[85%]'
                    : 'text-fg-body max-w-full'
                }
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  className="prose prose-sm dark:prose-invert max-w-none"
                >
                  {msg.content}
                </ReactMarkdown>
              </div>
            </div>
          ))
        )}

        {isSending && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-surface-sunken text-fg-body">
              <BrandLoader size="xs" className="!h-4 !w-4" />
              <span className="text-xs animate-fadePulse">{t('Thinking...')}</span>
            </div>
          </div>
        )}
      </div>

      <div
        data-tour-id="ds-test-input"
        className="flex-shrink-0 border-t border-hairline-soft px-3 py-2.5"
      >
        <div className="flex items-center gap-2">
          <Input
            type="text"
            placeholder={t('Enter your prompt...')}
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className="flex-1"
          />
          {/*
            A BUTTON THAT READS AS A BUTTON.

            This was the paper plane and nothing else, and an icon-only button
            beside a full-height input is a tall narrow tile — portrait where
            every other button in the app is landscape, and silent about what
            it does. Icon AND label, so it matches the send buttons elsewhere
            and says what pressing it means.

            `flex-shrink-0` because the row is a flex line: without it the
            button gives up its own width to the input as the pane narrows,
            and the label wraps or clips.
          */}
          <Button
            variant="primary"
            size="small"
            onClick={onSend}
            disabled={disabled || isSending || input.trim() === ''}
            icon={<PiPaperPlaneLight className="h-4 w-4" />}
            className="flex-shrink-0"
          >
            {t('Send')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataSourceAssistantPane;
